import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AuthCard, Button } from '@sentinel/ui';
import { isSafeReturnTo } from '@sentinel/auth-core';
import { z } from 'zod';
import { useWebAuthnLogin } from '../features/passkey/use-webauthn-login';

const search = z.object({ return_to: z.string().optional() });

type Step =
  | { kind: 'email' }
  | { kind: 'authenticating'; email: string }
  | { kind: 'error'; email: string; messageKey: string };

function PasskeyLoginPage() {
  const { t } = useTranslation('passkey');
  const { return_to } = useSearch({ from: '/login/passkey' });
  const target = isSafeReturnTo(return_to) ? return_to : '/launch';
  const passkey = useWebAuthnLogin();

  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setStep({ kind: 'authenticating', email: trimmed });
    try {
      const r = await passkey.mutateAsync({ email: trimmed, next: target });
      window.location.assign(r.redirect || target);
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') {
        setStep({ kind: 'email' });
        return;
      }
      const status = (err as { status?: number } | null)?.status;
      const messageKey =
        status === 429 ? 'rate_limited' : 'invalid_passkey';
      setStep({ kind: 'error', email: trimmed, messageKey });
    }
  }

  return (
    <AuthCard title={t('title')} subtitle={t('subtitle')}>
      {step.kind === 'error' ? (
        <Alert type="error" role="alert">
          {t(step.messageKey)}
        </Alert>
      ) : null}

      {step.kind === 'authenticating' ? (
        <p data-testid="passkey-tap-prompt">{t('tap_prompt')}</p>
      ) : (
        <form onSubmit={onSubmit}>
          <label>
            {t('email_label')}
            <input
              type="email"
              required
              autoComplete="username webauthn"
              data-testid="passkey-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('email_placeholder')}
            />
          </label>
          <Button type="submit" data-testid="passkey-continue">
            {step.kind === 'error' ? t('try_again') : t('continue')}
          </Button>
        </form>
      )}

      <p style={{ textAlign: 'center' }}>
        <Link
          to="/login"
          search={return_to ? { return_to } : {}}
          data-testid="use-password-link"
        >
          {t('use_password_instead')}
        </Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/login/passkey')({
  validateSearch: search,
  component: PasskeyLoginPage,
});
